import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import AccountDetails from 'components/common/AccountDetails';
import {
  PAYMENT_PROVIDERS,
  WALLESTER_ACCOUNT_TOOLTIP_TEXT,
  ACCOUNT_PROPERTIES,
  ACCOUNT_TOOLTIP_TEXT
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { isCryptoProvider } from 'services/utils';
import paymentStore from 'stores/paymentStore';
import { Card } from 'uikit/Card/Card';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import './Accounts.scss';

const Accounts = ({ userAccounts, transactionsStore, cardsStore, userStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Set current page path
    userStore.setCurrentPagePath('/accounts');
  }, [userStore]);

  const openAccountDetails = (account) => {
    userStore.setActiveAccountWalletNumber(account?.wallet_number);
  };

  const goToTransaction = (account) => {
    transactionsStore.setAccount(account);
    transactionsStore.setIsRedirectFromAccountCard(true);
    navigate('/transactions');
  };

  const goToPayment = (account) => {
    paymentStore.setSelectedAccount(account);
    navigate('/new-payment');
  };

  const goToCards = (account) => {
    cardsStore.setSelectedAccount(account);
    navigate('/cards');
  };

  const checkWallesterProvider = (account) => {
    return account.transfer_provider === PAYMENT_PROVIDERS.WALLESTER;
  };

  // Render full-screen AccountDetails instead of the dashboard when active
  if (userStore.activeAccountWalletNumber) {
    const activeAccount = userAccounts?.find((acc) => acc.wallet_number === userStore.activeAccountWalletNumber);
    // Keep showing AccountDetails even if account is temporarily not found during update
    // This prevents unwanted redirects during data updates
    return (
      <section className={'right-section dashboard'}>
        <AccountDetails
          accountData={activeAccount || { wallet_number: userStore.activeAccountWalletNumber }}
          setActiveAccountDetails={(walletNumber) => userStore.setActiveAccountWalletNumber(walletNumber)}
        />
      </section>
    );
  }

  return (
    <section className={'page-container accounts'}>
      <NewContainer
        className={'accounts-container content-card-wrapper'}
        header={i18n.getMessage('container.accounts')}
      >
        <div className={'card-wrapper'}>
          {userAccounts?.map((account) => {
            const isWallesterWallet = checkWallesterProvider(account);
            const isManualWallet =
              account.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
              account.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
              account.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
              account.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
              account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
              account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API;
            const isCryptoWallet = isCryptoProvider(account.transfer_provider);

            return (
              <Card
                key={account[ACCOUNT_PROPERTIES.ACCOUNT_NUMBER]}
                type={'card'}
                isCardAccount={isWallesterWallet}
                isManualAccount={isManualWallet}
                isCryptoAccount={isCryptoWallet}
                account={account}
                balance={account[ACCOUNT_PROPERTIES.AVAILABLE]}
                balanceDetails={account?.availableDetails}
                reservedDetails={account?.onHoldDetails}
                currency={account[ACCOUNT_PROPERTIES.CURRENCY]}
                networkCode={account[ACCOUNT_PROPERTIES.NETWORK]}
                reserved={account[ACCOUNT_PROPERTIES.ON_HOLD]}
                buttonText={'Review account'}
                onExclaimPointClick={() => openAccountDetails(account)}
                onPaymentClick={isWallesterWallet ? () => goToCards(account) : () => goToPayment(account)}
                onTransactionClick={() => goToTransaction(account)}
                tooltipText={isWallesterWallet ? WALLESTER_ACCOUNT_TOOLTIP_TEXT : ACCOUNT_TOOLTIP_TEXT}
              />
            );
          })}
        </div>
      </NewContainer>
    </section>
  );
};

Accounts.propTypes = {
  transactionsStore: MobXPropTypes.observableObject,
  cardsStore: MobXPropTypes.observableObject,
  userAccounts: PropTypes.array,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userAccounts: stores.userStore?.userAccounts,
  transactionsStore: stores.transactionsStore,
  cardsStore: stores.cardsStore,
  userStore: stores.userStore
}))(observer(Accounts));
